/**
 * Devoluciones: part of a sale coming back from a customer, and goods going back to a supplier.
 *
 * A void undoes a whole sale and an annulment a whole purchase, and both are refused once too
 * much has happened since. These are the everyday version: one bottle of three, a sack that
 * turned out torn, a box of expired biscuits the distributor takes back. They are new facts,
 * not erasures, so they are always possible.
 */

import {
  ZERO,
  ZERO_COST,
  ZERO_QTY,
  addMoney,
  addQty,
  centavos,
  costOf,
  formatMoney,
  mulDiv,
  orderKey,
  repartirProporcional,
  saleToBase,
  subMoney,
  subQty,
  weightedAverageCost,
  type AnyDomainEvent,
  type Centavos,
  type Milli,
  type UnitCost,
} from '@abastece/shared';
import { credit, debit, entry, type JournalLine } from '../ledger/journal.js';
import type { AccountKey } from '../ledger/accounts.js';
import { cuentaCerradaParaSiempre, cuentaDeTransferencia } from './bancos.js';
import { bajarDeuda } from './fiado.js';
import { adondeVuelve, ajusteVendidoAntes, consumirLotesFefo, cuentaInventario, devolverLotes, yaContado, type Recibido } from '../inventario.js';
import {
  dineroDeVenta,
  existenciaId,
  fiadoDeVenta,
  loteId,
  pendienteDeCompra,
  type ApplyResult,
  type CompraLinea,
  type Devolucion,
  type DevolucionCompra,
  type DevolucionLinea,
  type Effect,
  type Lote,
  type Reader,
  type Venta,
  type VentaLinea,
  type VentaPago,
} from '../state.js';

export async function reduceDevolucion(
  event: AnyDomainEvent,
  read: Reader,
): Promise<ApplyResult | null> {
  switch (event.type) {
    case 'devolucion.registrada':
      return devolverVenta(event, read, orderKey(event));
    case 'compra.devuelta':
      return devolverCompra(event, read, orderKey(event));
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Lo que vale lo que se devuelve
// ---------------------------------------------------------------------------

/**
 * What part of a sale's line is worth: from `desde` to `hasta` sale units already returned.
 *
 * Worked out as the difference between two running totals rather than on the piece alone, so
 * it telescopes: a line returned in three goes gives back exactly what the whole line was sold
 * for, to the centavo, and restocks exactly the quantity that left. The sale's discount is
 * spread over its lines the way the profit report spreads it, so the two always agree.
 *
 * Shared by the reducer and the screen, so what the customer is told is what gets booked.
 */
export function valorDevuelto(
  venta: Venta,
  indice: number,
  desde: Milli,
  hasta: Milli,
): { bruto: Centavos; neto: Centavos; costo: Centavos; base: Milli } {
  const l = venta.lineas[indice]!;
  const descuentos = repartirProporcional(
    venta.descuento,
    venta.lineas.map((x) => x.subtotal),
  );
  const neto = subMoney(l.subtotal, descuentos[indice] ?? ZERO);
  const hastaQ = (q: Milli) => ({
    bruto: l.cantidad === 0 ? ZERO : mulDiv(l.subtotal, q, l.cantidad),
    neto: l.cantidad === 0 ? ZERO : mulDiv(neto, q, l.cantidad),
    base: saleToBase(q, l.factor),
  });
  const a = hastaQ(desde);
  const b = hastaQ(hasta);
  return {
    bruto: subMoney(b.bruto, a.bruto),
    neto: subMoney(b.neto, a.neto),
    costo: subMoney(costOf(l.costoUnitarioPosteado, b.base), costOf(l.costoUnitarioPosteado, a.base)),
    base: subQty(b.base, a.base),
  };
}

/** Sale units of a line that can still come back. */
export const quedaPorDevolver = (l: VentaLinea): Milli => subQty(l.cantidad, l.devuelta ?? ZERO_QTY);

/** Which way what comes back of a sale goes (`repartirDevolucion`). */
export interface RepartoDevolucion {
  /** The way the return says: out of the drawer, the bank or the wallet, or off the client's account. */
  devuelto: Centavos;
  /** Against a transfer that never arrived: the loss it booked comes back with the goods. */
  aPerdida: Centavos;
  /** The centavos a sale rounded down never took in, back to «Redondeo» with the last of it. */
  aRedondeo: Centavos;
  /** What can go back no way yet. */
  falta: Centavos;
}

/**
 * How `neto` more of a sale goes back (2026-10-20). As far as the sale was really paid, fiado or
 * money in hand, the way the return says: as money, never more than the sale took in as money and
 * has not given back already; onto the client's account, never more than its fiado and its money
 * together, less what came back of them before. Past that, against a transfer that never arrived:
 * the goods coming back undo the loss, as a void does, and nobody is credited. Past that, a transfer
 * still waiting in the queue, which is not money in hand yet: it goes back once it is seen at the
 * bank, or marked as never arrived. Onto the account it used to go whole, so a screenshot that never
 * paid became credit to spend as fiado, with the transfer booked lost on top.
 *
 * A sale rounded down took in a few centavos less than its goods were worth: the return that brings
 * back the last of it gives back what is left, and those centavos go back to «Redondeo». It was
 * refused, and the last of such a sale could never come back.
 *
 * Shared by the reducer and the screen, so what the customer is told is what gets booked.
 */
export function repartirDevolucion(
  venta: Venta,
  /** What earlier returns of the sale gave back as money (`Devolucion.total` of those not onto the account). */
  yaEnDinero: number,
  neto: Centavos,
  reembolso: Devolucion['reembolso'],
  /** Whether this return brings back the last of every line. */
  completa: boolean,
): RepartoDevolucion {
  const recuperado = venta.pagos.reduce((a, x) => a + (x.recuperado ?? 0), 0);
  const perdido = venta.pagos.reduce((a, x) => (x.estado === 'rechazado' ? a + x.monto - (x.recuperado ?? 0) : a), 0);
  const cabe =
    reembolso === 'credito'
      ? dineroDeVenta(venta) + fiadoDeVenta(venta) - ((venta.devuelto ?? 0) - recuperado)
      : dineroDeVenta(venta) - yaEnDinero;
  const devuelto = Math.min(neto, Math.max(0, cabe));
  let resto = neto - devuelto;
  const aPerdida = reembolso === 'credito' ? Math.min(resto, Math.max(0, perdido)) : 0;
  resto -= aPerdida;
  const aRedondeo = completa && venta.redondeo < 0 ? Math.min(resto, -venta.redondeo) : 0;
  resto -= aRedondeo;
  return { devuelto: centavos(devuelto), aPerdida: centavos(aPerdida), aRedondeo: centavos(aRedondeo), falta: centavos(resto) };
}

/** What `aPerdida` of a return takes back of each transfer that never arrived, oldest payment first. */
function conLoRecuperado(pagos: readonly VentaPago[], aPerdida: Centavos): VentaPago[] {
  let queda: number = aPerdida;
  return pagos.map((x) => {
    if (x.estado !== 'rechazado' || queda <= 0) return x;
    const toma = Math.min(queda, x.monto - (x.recuperado ?? 0));
    if (toma <= 0) return x;
    queda -= toma;
    return { ...x, recuperado: centavos((x.recuperado ?? 0) + toma) };
  });
}

/** What a purchase line is worth to the supplier, the same telescoping way. */
export function valorDevueltoCompra(l: CompraLinea, desde: Milli, hasta: Milli): Centavos {
  if (l.cantidadBase === 0) return ZERO;
  return subMoney(mulDiv(l.costoCordobas, hasta, l.cantidadBase), mulDiv(l.costoCordobas, desde, l.cantidadBase));
}

// ---------------------------------------------------------------------------
// De un cliente
// ---------------------------------------------------------------------------

const CUENTA_REEMBOLSO: Record<'efectivo_cordobas' | 'credito' | 'transferencia' | 'billetera', AccountKey> = {
  // Out of the drawer the refund was paid from, which the corte then expects to hold less.
  efectivo_cordobas: 'CAJA_CORDOBAS',
  // Off what the client owes.
  credito: 'CUENTAS_POR_COBRAR',
  transferencia: 'BANCOS',
  billetera: 'BILLETERA_MOVIL',
};

async function devolverVenta(
  event: Extract<AnyDomainEvent, { type: 'devolucion.registrada' }>,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const p = event.payload;
  const rechazo = (reason: string): ApplyResult => ({ effects: [], rejections: [{ eventId: event.id, reason }] });

  if (await read.devolucion(p.devolucionId)) return rechazo('Esa devolución ya fue registrada');
  const cerrada = cuentaCerradaParaSiempre(await read.config(), p.cuenta);
  if (cerrada) return rechazo(cerrada);
  const venta = await read.venta(p.ventaId);
  if (!venta) return rechazo('Esa venta no existe');
  if (venta.anulada) return rechazo('Esa venta está anulada: no queda nada que devolver');

  // The same line twice in one return is one line with the quantities added.
  const pedidas = new Map<number, { cantidad: number; reingresar: boolean }>();
  for (const l of p.lineas) {
    const antes = pedidas.get(l.indice);
    pedidas.set(l.indice, {
      cantidad: (antes?.cantidad ?? 0) + l.cantidad,
      reingresar: antes ? antes.reingresar && l.reingresar : l.reingresar,
    });
  }
  for (const [indice, { cantidad }] of pedidas) {
    const linea = venta.lineas[indice];
    if (!linea) return rechazo('Esa línea no está en la venta');
    if (cantidad > quedaPorDevolver(linea)) return rechazo('Se devuelve más de lo que se vendió');
  }

  const cliente = venta.clienteId ? await read.cliente(venta.clienteId) : undefined;
  if (p.reembolso === 'credito' && !cliente) {
    return rechazo('Solo se puede devolver a la cuenta de una venta hecha a un cliente');
  }
  const sesion = p.sesionId ? await read.sesionCaja(p.sesionId) : undefined;
  if (p.reembolso === 'efectivo_cordobas') {
    if (!sesion) return rechazo('Un reembolso en efectivo sale de una caja abierta');
    if (sesion.estado !== 'abierta') return rechazo('La caja de este reembolso ya fue cerrada');
  }

  const effects: Effect[] = [];
  const lineas: DevolucionLinea[] = [];
  const lineasVenta = venta.lineas.map((l) => ({ ...l }));
  let bruto: Centavos = ZERO;
  let neto: Centavos = ZERO;
  let costoReingresado: Centavos = ZERO;
  let costoMerma: Centavos = ZERO;
  const reingresos = new Map<string, { cantidad: Milli; costoUnitario: UnitCost }[]>();
  /** What each product's restocked part cost, as the entry books it. */
  const costoReingresadoDe = new Map<string, Centavos>();

  for (const [indice, { cantidad, reingresar }] of [...pedidas].sort((a, b) => a[0] - b[0])) {
    const l = lineasVenta[indice]!;
    const desde = l.devuelta ?? ZERO_QTY;
    const hasta = addQty(desde, cantidad as Milli);
    const v = valorDevuelto(venta, indice, desde, hasta);
    l.devuelta = hasta;

    bruto = addMoney(bruto, v.bruto);
    neto = addMoney(neto, v.neto);
    if (l.productoId && v.costo !== 0) {
      if (reingresar) {
        costoReingresado = addMoney(costoReingresado, v.costo);
        costoReingresadoDe.set(l.productoId, addMoney(costoReingresadoDe.get(l.productoId) ?? ZERO, v.costo));
      } else costoMerma = addMoney(costoMerma, v.costo);
    }
    if (l.productoId && reingresar && v.base > 0) {
      const lista = reingresos.get(l.productoId) ?? [];
      lista.push({ cantidad: v.base, costoUnitario: l.costoUnitarioPosteado });
      reingresos.set(l.productoId, lista);
    }

    lineas.push({
      indice,
      productoId: l.productoId,
      descripcion: l.descripcion,
      cantidad: cantidad as Milli,
      factor: l.factor,
      reingresar,
      valor: v.neto,
      costo: v.costo,
    });
  }

  /**
   * Money goes back the way it came (2026-10-13): as money, never more than the sale took in as
   * money and has not given back already (`dineroDeVenta`). What it put on the client's account goes
   * back onto the account, and a transfer still waiting in the queue is not money in hand yet. The
   * way back used to be free: a sale put on fiado and returned for cash at once gave the drawer's
   * money to somebody who still owed it, the corte balanced, and a screenshot never paid could be
   * turned into cash by bringing the goods back. Onto the account too, since 2026-10-20
   * (`repartirDevolucion`).
   */
  const yaEnDinero = (await read.devolucionesDeVenta(venta.id))
    .filter((d) => d.reembolso !== 'credito')
    .reduce((s, d) => s + d.total, 0);
  const completa = lineasVenta.every((l) => (l.devuelta ?? ZERO_QTY) >= l.cantidad);
  const reparto = repartirDevolucion(venta, yaEnDinero, neto, p.reembolso, completa);
  if (reparto.falta > 0) {
    if (p.reembolso === 'credito') {
      return rechazo(
        'Lo pagado con una transferencia que todavía no se confirma no vuelve a la cuenta: confírmela, o anote que no llegó, y devuélvalo después',
      );
    }
    const enDinero = dineroDeVenta(venta);
    const queda = Math.max(0, enDinero - yaEnDinero) as Centavos;
    const aCuenta = cliente ? 'el resto, a la cuenta del cliente' : 'no hay cuenta de cliente a la cual devolver el resto';
    const sinDinero = cliente ? 'va a la cuenta del cliente' : 'su pago todavía no se confirma';
    return rechazo(
      queda > 0
        ? `En dinero se devuelve a lo más ${formatMoney(queda)}, lo que esta venta recibió en dinero; ${aCuenta}`
        : enDinero > 0
          ? `Lo que esta venta recibió en dinero ya se devolvió: ${sinDinero}`
          : `Esta venta no recibió dinero que devolver: ${sinDinero}`,
    );
  }

  // Back on the shelf where it was sold from, at the cost it left with — the same as a void; off a
  // route settled since, into the shop (`adondeVuelve`).
  const donde = adondeVuelve(venta, await read.sesionCaja(venta.sesionId), p.reembolso === 'efectivo_cordobas' ? sesion : undefined);
  let costoContado: Centavos = ZERO;
  const devueltos: Recibido[] = [];
  for (const [productoId, partes] of reingresos) {
    const actual = await read.existencia(productoId, donde);
    // A count already found it back on the shelf: it stays where the count left it.
    if (yaContado(actual, event)) {
      costoContado = addMoney(costoContado, costoReingresadoDe.get(productoId) ?? ZERO);
      continue;
    }
    let cantidad = actual?.cantidad ?? ZERO_QTY;
    let costoPromedio = actual?.costoPromedio ?? ZERO_COST;
    let total = ZERO_QTY;
    for (const parte of partes) {
      costoPromedio = weightedAverageCost(cantidad, costoPromedio, parte.cantidad, parte.costoUnitario);
      cantidad = addQty(cantidad, parte.cantidad);
      total = addQty(total, parte.cantidad);
    }
    devueltos.push({
      antes: { cantidad: actual?.cantidad ?? ZERO_QTY, costoPromedio: actual?.costoPromedio ?? ZERO_COST },
      despues: { cantidad, costoPromedio },
      entrante: costoReingresadoDe.get(productoId) ?? ZERO,
      cuenta: cuentaInventario(donde),
    });
    const clave = existenciaId(productoId, donde);
    effects.push({
      kind: 'existencia.set',
      id: clave,
      row: {
        id: clave,
        productoId,
        ubicacion: donde,
        cantidad,
        costoPromedio,
        ultimoMovimientoId: p.devolucionId,
        rev,
      },
    });
    effects.push(...devolverLotes(await read.lotesDe(productoId, donde), total, rev));
  }

  /**
   * The money. What was sold stays sold in `Ventas`; what came back is its own figure, beside
   * the discounts, and the discount that came with those lines goes back where it was booked.
   */
  const descuento = subMoney(bruto, neto);
  // Something given away free comes back worth nothing, and an entry of nothing is no entry.
  if (bruto > 0) {
    const lineasAsiento: JournalLine[] = [debit('DEVOLUCIONES_VENTAS', bruto, venta.ticket)];
    if (descuento > 0) lineasAsiento.push(credit('DESCUENTOS', descuento));
    if (descuento < 0) lineasAsiento.push(debit('DESCUENTOS', centavos(-descuento)));
    // A refund by transfer, out of the account it went from (`bancos.ts`).
    if (reparto.devuelto > 0) {
      lineasAsiento.push(credit(p.reembolso === 'transferencia' ? cuentaDeTransferencia(p.cuenta) : CUENTA_REEMBOLSO[p.reembolso], reparto.devuelto));
    }
    if (reparto.aPerdida > 0) lineasAsiento.push(credit('TRANSFERENCIAS_PERDIDAS', reparto.aPerdida, 'Nunca llegó; la mercadería volvió'));
    if (reparto.aRedondeo > 0) lineasAsiento.push(credit('REDONDEO', reparto.aRedondeo, 'El redondeo, con lo último de la venta'));
    effects.push({
      kind: 'asiento',
      entry: entry(event.id, p.fecha, `Devolución de ${venta.ticket}: ${p.motivo}`, lineasAsiento),
    });
  }

  // The cost goes back out of cost of sales: to stock for what can be sold again, to merma for
  // what came back broken.
  const lineasCosto: JournalLine[] = [];
  if (costoReingresado > costoContado) {
    lineasCosto.push(debit(cuentaInventario(donde), subMoney(costoReingresado, costoContado)));
  }
  if (costoContado > 0) lineasCosto.push(debit('FALTANTE_INVENTARIO', costoContado, 'Ya contado'));
  if (costoMerma > 0) lineasCosto.push(debit('MERMA', costoMerma, 'Devuelto dañado'));
  if (lineasCosto.length > 0) {
    lineasCosto.push(credit('COSTO_VENTAS', addMoney(costoReingresado, costoMerma)));
    effects.push({
      kind: 'asiento',
      entry: entry(event.id, p.fecha, `Costo devuelto de ${venta.ticket}`, lineasCosto),
    });
  }
  const ajuste = ajusteVendidoAntes(event.id, p.fecha, devueltos);
  if (ajuste) effects.push(ajuste);

  effects.push({
    kind: 'venta.patch',
    id: venta.id,
    rev,
    patch: {
      lineas: lineasVenta,
      devuelto: addMoney(venta.devuelto ?? ZERO, neto),
      ...(reparto.aPerdida > 0 ? { pagos: conLoRecuperado(venta.pagos, reparto.aPerdida) } : {}),
    },
  });

  if (p.reembolso === 'credito' && cliente && reparto.devuelto > 0) {
    // Off fiado given up on, it was not lost either (`bajarDeuda`).
    const baja = bajarDeuda(cliente, reparto.devuelto, event.id, p.fecha, `devolución de ${venta.ticket}`);
    effects.push({ kind: 'cliente.patch', id: cliente.id, rev, patch: baja.patch });
    if (baja.asiento) effects.push(baja.asiento);
  }
  if (p.reembolso === 'efectivo_cordobas' && sesion) {
    effects.push({
      kind: 'sesion.patch',
      id: sesion.id,
      rev,
      patch: { devoluciones: addMoney(sesion.devoluciones ?? ZERO, reparto.devuelto) },
    });
  }

  effects.push({
    kind: 'devolucion.upsert',
    id: p.devolucionId,
    row: {
      id: p.devolucionId,
      ventaId: venta.id,
      ticket: venta.ticket,
      clienteId: venta.clienteId,
      fecha: p.fecha,
      sesionId: p.reembolso === 'efectivo_cordobas' ? (p.sesionId ?? null) : null,
      lineas,
      // What went back the way it says: what a client's statement takes off, and money gives back.
      total: reparto.devuelto,
      reembolso: p.reembolso,
      ...(p.cuenta ? { cuenta: p.cuenta } : {}),
      motivo: p.motivo,
      rev,
    },
  });

  return { effects, rejections: [] };
}

// ---------------------------------------------------------------------------
// A un proveedor
// ---------------------------------------------------------------------------

const CUENTA_LIQUIDACION: Record<'descontar' | 'efectivo_cordobas' | 'transferencia' | 'billetera', AccountKey> = {
  descontar: 'CUENTAS_POR_PAGAR',
  // General cash, never the drawer: the same rule as paying a supplier.
  efectivo_cordobas: 'CAJA_FUERTE',
  transferencia: 'BANCOS',
  billetera: 'BILLETERA_MOVIL',
};

async function devolverCompra(
  event: Extract<AnyDomainEvent, { type: 'compra.devuelta' }>,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const p = event.payload;
  const rechazo = (reason: string): ApplyResult => ({ effects: [], rejections: [{ eventId: event.id, reason }] });
  const cerrada = cuentaCerradaParaSiempre(await read.config(), p.cuenta);
  if (cerrada) return rechazo(cerrada);

  if (await read.devolucionCompra(p.devolucionId)) return rechazo('Esa devolución ya fue registrada');
  const compra = await read.compra(p.compraId);
  if (!compra) return rechazo('Esa compra no existe');
  if (compra.anulada) return rechazo('Esa compra está anulada');

  const pedidas = new Map<number, number>();
  for (const l of p.lineas) pedidas.set(l.indice, (pedidas.get(l.indice) ?? 0) + l.cantidadBase);
  for (const [indice, cantidad] of pedidas) {
    const linea = compra.lineas[indice];
    if (!linea) return rechazo('Esa línea no está en la compra');
    if (cantidad > subQty(linea.cantidadBase, linea.devuelta ?? ZERO_QTY)) {
      return rechazo('Se devuelve más de lo que se compró');
    }
  }

  const lineasCompra = compra.lineas.map((l) => ({ ...l }));
  const lineas: DevolucionCompra['lineas'] = [];
  const effects: Effect[] = [];
  let valor: Centavos = ZERO;
  let costo: Centavos = ZERO;
  /** Stock as it stands after each line, for a product returned on two lines of one purchase. */
  const pendiente = new Map<string, { cantidad: Milli; costoPromedio: UnitCost; base: Milli }>();
  /**
   * Sent back before a count that reached the books first: the count already found it gone, so
   * the stock stays as counted and its cost goes against what that count found (`yaContado`).
   */
  const contados = new Set<string>();
  let costoContado: Centavos = ZERO;

  for (const [indice, cantidad] of [...pedidas].sort((a, b) => a[0] - b[0])) {
    const l = lineasCompra[indice]!;
    const desde = l.devuelta ?? ZERO_QTY;
    const hasta = addQty(desde, cantidad as Milli);
    const v = valorDevueltoCompra(l, desde, hasta);
    l.devuelta = hasta;

    const clave = existenciaId(l.productoId, compra.ubicacion);
    let estado = pendiente.get(clave);
    if (!estado) {
      const actual = await read.existencia(l.productoId, compra.ubicacion);
      if (yaContado(actual, event)) contados.add(clave);
      estado = {
        cantidad: actual?.cantidad ?? ZERO_QTY,
        costoPromedio: actual?.costoPromedio ?? ZERO_COST,
        base: ZERO_QTY,
      };
    }
    // Out at the running average, like any exit: the average does not move.
    const c = costOf(estado.costoPromedio, cantidad as Milli);
    if (contados.has(clave)) costoContado = addMoney(costoContado, c);
    pendiente.set(clave, {
      cantidad: subQty(estado.cantidad, cantidad as Milli),
      costoPromedio: estado.costoPromedio,
      base: addQty(estado.base, cantidad as Milli),
    });

    valor = addMoney(valor, v);
    costo = addMoney(costo, c);
    lineas.push({ indice, productoId: l.productoId, cantidadBase: cantidad as Milli, valor: v, costo: c });
  }

  if (p.liquidacion === 'descontar') {
    if (compra.formaPago !== 'credito') {
      return rechazo('Esa compra se pagó al contado: el proveedor tiene que devolver el dinero');
    }
    if (valor > pendienteDeCompra(compra)) {
      return rechazo('Se descuenta más de lo que se debe de esa compra; pida el resto en dinero');
    }
  }

  for (const [clave, estado] of pendiente) {
    if (contados.has(clave)) continue;
    const productoId = clave.slice(0, clave.indexOf(':'));
    effects.push({
      kind: 'existencia.set',
      id: clave,
      row: {
        id: clave,
        productoId,
        ubicacion: compra.ubicacion,
        cantidad: estado.cantidad,
        costoPromedio: estado.costoPromedio,
        ultimoMovimientoId: p.devolucionId,
        rev,
      },
    });
  }

  /**
   * What goes back goes out of the batch it came in (`CompraLinea.lote`): sending back December's
   * syrup used to take November's off the expiry board. What that batch no longer holds, and a
   * line with none, out of the earliest to expire, as any exit.
   */
  const lotes = new Map<string, Lote[]>();
  const tocados = new Map<string, Lote>();
  for (const { indice, productoId, cantidadBase } of lineas) {
    if (contados.has(existenciaId(productoId, compra.ubicacion))) continue;
    const lista = lotes.get(productoId) ?? (await read.lotesDe(productoId, compra.ubicacion)).map((x) => ({ ...x }));
    lotes.set(productoId, lista);
    let resta = cantidadBase;
    const suyo = lineasCompra[indice]!.lote;
    const propio = suyo ? lista.find((x) => x.id === loteId(productoId, suyo.numero, compra.ubicacion)) : undefined;
    if (propio && propio.cantidadDisponible > 0) {
      const toma = Math.min(resta, propio.cantidadDisponible) as Milli;
      propio.cantidadDisponible = subQty(propio.cantidadDisponible, toma);
      propio.rev = rev;
      tocados.set(propio.id, propio);
      resta = subQty(resta, toma);
    }
    if (resta > 0) {
      for (const efecto of consumirLotesFefo(lista, resta, rev)) {
        const fila = lista.find((x) => x.id === efecto.id)!;
        Object.assign(fila, efecto.row);
        tocados.set(fila.id, fila);
      }
    }
  }
  for (const [id, row] of tocados) effects.push({ kind: 'lote.upsert', id, row: { ...row } });

  /**
   * The supplier credits what the purchase paid; the stock was carried at the average. The gap
   * between the two was never profit or loss from selling anything — it is a correction to what
   * the goods cost, so it goes back to cost of sales.
   */
  const diferencia = subMoney(valor, costo);
  if (valor > 0 || costo > 0) {
    const lineasAsiento: JournalLine[] = [
      debit(p.liquidacion === 'transferencia' ? cuentaDeTransferencia(p.cuenta) : CUENTA_LIQUIDACION[p.liquidacion], valor, p.motivo),
      credit(cuentaInventario(compra.ubicacion), subMoney(costo, costoContado)),
      credit('FALTANTE_INVENTARIO', costoContado, 'Ya contado'),
    ];
    if (diferencia > 0) lineasAsiento.push(credit('COSTO_VENTAS', diferencia, 'Diferencia de costo devuelto'));
    if (diferencia < 0) lineasAsiento.push(debit('COSTO_VENTAS', centavos(-diferencia), 'Diferencia de costo devuelto'));
    effects.push({
      kind: 'asiento',
      entry: entry(event.id, p.fecha, `Devolución al proveedor: ${p.motivo}`, lineasAsiento),
    });
  }

  effects.push({
    kind: 'compra.patch',
    id: compra.id,
    rev,
    patch: {
      lineas: lineasCompra,
      devuelto: addMoney(compra.devuelto ?? ZERO, valor),
      ...(p.liquidacion === 'descontar'
        ? { devueltoACuenta: addMoney(compra.devueltoACuenta ?? ZERO, valor) }
        : {}),
    },
  });

  effects.push({
    kind: 'devolucionCompra.upsert',
    id: p.devolucionId,
    row: {
      id: p.devolucionId,
      compraId: compra.id,
      proveedorId: compra.proveedorId,
      fecha: p.fecha,
      lineas,
      total: valor,
      liquidacion: p.liquidacion,
      ...(p.cuenta ? { cuenta: p.cuenta } : {}),
      motivo: p.motivo,
      rev,
    },
  });

  return { effects, rejections: [] };
}
